import { initializeApp } from 'firebase/app';
import { getDatabase, update } from 'firebase/database';
import { getStorage } from 'firebase/storage';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { ref, set, get } from 'firebase/database';
import { getFirestore, enableIndexedDbPersistence } from 'firebase/firestore';

function getCurrentTimestamp() {
    return new Date().toISOString();
}

// Development Configuration
const devConfig = {
    apiKey: "AIzaSyDVhQ7HlKkMUaZMAdzvd9fJSYd8EK_5ktk",
    authDomain: "bonusclub-dev.firebaseapp.com",
    databaseURL: "https://bonusclub-dev-default-rtdb.europe-west1.firebasedatabase.app",
    projectId: "bonusclub-dev",
    storageBucket: "bonusclub-dev.appspot.com",
    messagingSenderId: "177985771865",
    appId: "1:177985771865:web:2fc89d44b4688bf66ed448"
};

// Production Configuration  
const prodConfig = {
    apiKey: process.env.REACT_APP_PROD_API_KEY || "AIzaSyDc4GbsKzfOy6YbvgNjA6svYzDxV1e2w58",
    authDomain: process.env.REACT_APP_PROD_AUTH_DOMAIN || "bonusclub-ab5e2.firebaseapp.com",
    databaseURL: process.env.REACT_APP_PROD_DATABASE_URL || "https://bonusclub-ab5e2-default-rtdb.europe-west1.firebasedatabase.app",
    projectId: process.env.REACT_APP_PROD_PROJECT_ID || "bonusclub-ab5e2",
    storageBucket: process.env.REACT_APP_PROD_STORAGE_BUCKET || "bonusclub-ab5e2.appspot.com",
    messagingSenderId: process.env.REACT_APP_PROD_MESSAGING_SENDER_ID || "248616276773",
    appId: process.env.REACT_APP_PROD_APP_ID || "1:248616276773:web:f3d8e7c06e15ca552a40cc"
};

// Determine which config to use - simplified logic
let firebaseConfig;
if (process.env.REACT_APP_ENV === 'production') {
    firebaseConfig = prodConfig;
} else {
    firebaseConfig = devConfig;
}

const app = initializeApp(firebaseConfig);
const database = getDatabase(app);
const storage = getStorage(app);
const auth = getAuth(app);
const firestore = getFirestore(app);

// Aktiviere Offline-Persistenz für Firestore
enableIndexedDbPersistence(firestore)
  .catch((err) => {
    if (err.code === 'failed-precondition') {
      console.warn('Firestore Persistence konnte nicht aktiviert werden: Mehrere Tabs offen');
    } else if (err.code === 'unimplemented') {
      console.warn('Firestore Persistence wird von diesem Browser nicht unterstützt');
    }
  });

export { app, database, storage, auth, firestore };



export function addSurveyConfig(userId, surveyConfig) {
    const userSurveyConfigRef = ref(database, 'surveyConfigs/' + userId);
    return update(userSurveyConfigRef, surveyConfig);  // Use the update method here
}

export function getSpecificSurveyConfig(userId, callback) {
    const userSurveyConfigRef = ref(database, 'surveyConfigs/' + userId);
    
    userSurveyConfigRef.on('value', (snapshot) => {
        const data = snapshot.val();
        callback(data);
    });
}

export function addUserEntry(userId) {
    const userRef = ref(database, `Users/${userId}`);
    const now = getCurrentTimestamp();
    
    // Zuerst Daten abrufen, um zu prüfen, ob der Benutzer bereits existiert
    return get(userRef).then(snapshot => {
        const auth = getAuth();
        const user = auth.currentUser;
        
        if (user) {
            // Vollständige Benutzerdaten speichern oder aktualisieren
            return update(userRef, {
                email: user.email,
                name: user.displayName || "",
                signupTimestamp: now,
                hasSettings: false,
                isPremium: false,
                sessionCount: snapshot.exists() ? (snapshot.val().sessionCount || 1) : 1,
                lastLogin: now,
                lastAccess: now
            });
        } else {
            // Minimaler Eintrag, falls kein aktueller Benutzer verfügbar ist
            return update(userRef, { hasSettings: false });
        }
    });
}

export function setUserPremiumStatus(userId, isPremium = false) {
    const userRef = ref(database, `Users/${userId}`);
    return update(userRef, { isPremium });
}



export function getSurveyConfigs(callback) {
    const surveyConfigsRef = ref(database, 'surveyConfigs');
    surveyConfigsRef.on('value', (snapshot) => {
        const data = snapshot.val();
        callback(data);
    });
}

// firebaseConfig.js
export const getSurveyConfig = async (userId) => {
    // Holen Sie sich die Daten aus Firebase basierend auf der Benutzer-ID.
    const userRef = ref(database, `users/${userId}`);
    let result = null;

    await userRef.once('value', (snapshot) => {
        if (snapshot.exists()) {
            result = snapshot.val();
        } else {
            console.log("No such document!");
        }
    });

    return result;
};

onAuthStateChanged(auth, user => {
    if (user) {
        const userId = user.uid;
        // Referenz auf den Benutzer in der Datenbank
        const userRef = ref(getDatabase(), `Users/${userId}`);
        get(userRef).then(snapshot => {
            const now = getCurrentTimestamp();
            if (!snapshot.exists()) {
                // Wenn der Benutzer zum ersten Mal registriert wird, füge zusätzliche Daten hinzu
                set(userRef, {
                    email: user.email,
                    name: user.displayName,
                    signupTimestamp: now,
                    hasSettings: false,
                    isPremium: false,
                    sessionCount: 1, // Initialisiere den Session-Counter
                    lastLogin: now,
                    lastAccess: now,
                }).then(() => console.log('Benutzerdaten bei der Registrierung gespeichert.'))
                .catch(error => console.error('Fehler beim Speichern der Benutzerdaten:', error));
            } else {
                // Extrahiere den letzten Zugriffszeitstempel
                const lastAccess = new Date(snapshot.val().lastAccess).getTime();
                const currentTime = new Date(now).getTime();
                const diff = currentTime - lastAccess;
                const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 Minuten in Millisekunden

                // Überprüfe, ob die Session als neu betrachtet werden sollte
                if (diff > SESSION_TIMEOUT) {
                    const currentSessionCount = snapshot.val().sessionCount || 0;
                    update(userRef, {
                        sessionCount: currentSessionCount + 1, // Inkrementiere den Session-Counter
                        lastLogin: now,
                    }).then(() => console.log('Session-Informationen aktualisiert.'))
                    .catch(error => console.error('Fehler beim Aktualisieren der Session-Informationen:', error));
                }

                // Aktualisiere den letzten Zugriffszeitstempel unabhängig davon, ob es eine neue Session ist oder nicht
                update(userRef, { lastAccess: now });
            }
        });
    }
});




